import { EventEmitter, inject, Inject, Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { NGXLogger } from "ngx-logger";
import { Pageable } from "../../../models/src/lib/base";
import { Template } from "../../../models/src/lib/template";
import { User } from "../../../models/src/lib/user";
import { AUTH_SVC, AuthService } from "@company-box/services";
import { ENVIRONMENT } from "../../../models/src/lib/injection.tokens";
import { Group } from "../../../models/src/lib/group";
import { ValidationService } from "./validation.service";
import { AlertService } from "./alert.service";
import { lastValueFrom } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class UserService {
  private user: User | undefined;
  public update$ = new EventEmitter<User>();
  protected httpClient: HttpClient = inject(HttpClient);
  protected authService: AuthService = inject(AUTH_SVC);
  protected validationService: ValidationService = inject(ValidationService);
  protected alertSvc: AlertService = inject(AlertService);
  protected logger: NGXLogger = inject(NGXLogger);
  protected environment: any = inject(ENVIRONMENT);

  constructor() {}

  async getCurrentUser(): Promise<User> {
    this.logger.debug("session user", this.user);
    if (this.user) {
      return Promise.resolve(this.user);
    }
    if (this.authService.getToken()) {
      const url = `${this.environment.apiHost}/user/user`;
      try {
        const res = await lastValueFrom(this.httpClient.get(url)).then();
        this.user = new User().deserialize(res);
        return this.user;
      } catch (error) {
        this.logger.error(error);
        return await Promise.reject(error);
      }
    } else {
      return Promise.reject("Not logged in");
    }
  }

  async getUser(userId: number, forceReload?: boolean): Promise<User> {
    if (userId === 0) {
      return Promise.resolve(new User());
    }
    const onFulfilled = (res: any) => {
      return new User().deserialize(res);
    };
    const onRejected = (error: HttpErrorResponse) => {
      this.logger.error(error);
      return Promise.reject(error);
    };
    if (forceReload) {
      try {
        const res = await lastValueFrom(
          this.httpClient.post(`${this.environment.apiHost}/user/user/${userId}/reload`, {})
        ).then();
        return onFulfilled(res);
      } catch (error: any) {
        return onRejected(error);
      }
    } else {
      try {
        const res = await lastValueFrom(this.httpClient.get(`${this.environment.apiHost}/user/user/${userId}`)).then();
        return onFulfilled(res);
      } catch (error: any) {
        return onRejected(error);
      }
    }
  }
  async getTemplate(templateCode: string): Promise<Template> {
    if (templateCode === "") {
      return Promise.resolve(new Template());
    }
    const onFulfilled = (res: any) => {
      return new Template().deserialize(res);
    };
    const onRejected = (error: HttpErrorResponse) => {
      this.logger.error(error);
      return Promise.reject(error);
    };
    try {
      const res = await lastValueFrom(
        this.httpClient.get(`${this.environment.apiHost}/user/user/templates/${templateCode}`)
      ).then();
      return onFulfilled(res);
    } catch (error: any) {
      return onRejected(error);
    }
  }
  async getTemplateReq(templateCode: string): Promise<Template> {
    if (templateCode === "") {
      return Promise.resolve(new Template());
    }
    const onFulfilled = (res: any) => {
      return new Template().deserialize(res);
    };
    const onRejected = (error: HttpErrorResponse) => {
      this.logger.error(error);
      return Promise.reject(error);
    };
    try {
      const res = await lastValueFrom(
        this.httpClient.get(`${this.environment.apiHost}/user/user/templateReq/${templateCode}`)
      ).then();
      return onFulfilled(res);
    } catch (error: any) {
      return onRejected(error);
    }
  }
  async getUserByExternalId(userId: string): Promise<User> {
    return lastValueFrom(this.httpClient.get(`${this.environment.apiHost}/user/user/external/${userId}`))
      .then()
      .then((res: any) => {
        if (res) {
          return new User().deserialize(res);
        }
        return User.EMPTY;
      })
      .catch((error: HttpErrorResponse) => {
        this.logger.error(error);
        return Promise.reject(error);
      });
  }

  async getUserByExternalUsername(username: string): Promise<User> {
    return lastValueFrom(this.httpClient.get(`${this.environment.apiHost}/user/user/external/${username}`))
      .then()
      .then((res: any) => {
        if (res) {
          return new User().deserialize(res);
        }
        return User.EMPTY;
      })
      .catch((error: HttpErrorResponse) => {
        this.logger.error(error);
        return Promise.reject(error);
      });
  }

  async fixUser(user: User): Promise<User> {
    return lastValueFrom(this.httpClient.post(`${this.environment.apiHost}/user/user/shopify/fix`, user))
      .then()
      .then((res: any) => {
        if (res) {
          return new User().deserialize(res);
        }
        return User.EMPTY;
      })
      .catch((error: HttpErrorResponse) => {
        this.logger.error(error);
        return Promise.reject(error);
      });
  }

  async delete(userId: number) {
    return lastValueFrom(this.httpClient.delete(`${this.environment.apiHost}/user/user/${userId}`))
      .then()
      .then((res: any) => {
        return true;
      })
      .catch((error: HttpErrorResponse) => {
        this.logger.error(error);
        return Promise.reject(error);
      });
  }
  async deletetemp(templateCode: string) {
    return lastValueFrom(this.httpClient.delete(`${this.environment.apiHost}/user/user/templates/${templateCode}`))
      .then()
      .then((res: any) => {
        return true;
      })
      .catch((error: HttpErrorResponse) => {
        this.logger.error(error);
        return Promise.reject(error);
      });
  }
  async getAllUsers(page: number, pageSize: number, full: boolean): Promise<Pageable<User>> {
    let url = `${this.environment.apiHost}/user/user/list?`;
    if (page > 0) {
      const offset = page - 1;
      url += `offset=${offset}&limit=${pageSize}&`;
    }
    url += `full=${full}&sort=-lastLogin`;
    return lastValueFrom(this.httpClient.get(url))
      .then()
      .then((response: any) => {
        const list: User[] = [];
        (response.content as any[]).forEach((object) => {
          const user: User = new User().deserialize(object);
          list.push(user);
        });
        const newPage: Pageable<User> = new Pageable<User>().deserialize(response);
        newPage.content = list;
        this.logger.debug("Loaded users", newPage);
        return newPage;
      })
      .catch((error) => {
        this.logger.error(error);
        return Promise.reject(error);
      });
  }
  async getAllGroups(): Promise<Group[]> {
    const url = `${this.environment.apiHost}/user/user/groups/list`;

    return lastValueFrom(this.httpClient.get(url))
      .then()
      .then((response) => {
        const list: Group[] = [];
        (response as any[]).forEach((object) => {
          const group: Group = new Group().deserialize(object);
          list.push(group);
        });

        return list;
      })
      .catch((error) => {
        this.logger.error(error);
        return Promise.reject(error);
      });
  }
  async getAllUsersByGroupName(groupName: string): Promise<User[]> {
    const url = `${this.environment.apiHost}/user/user/list/${groupName}`;

    return lastValueFrom(this.httpClient.get(url))
      .then()
      .then((response) => {
        const list: User[] = [];
        (response as any[]).forEach((object) => {
          const user: User = new User().deserialize(object);
          list.push(user);
        });

        return list;
      })
      .catch((error) => {
        this.logger.error(error);
        return Promise.reject(error);
      });
  }

  async getAllTemplatesByGroupName(groupName: string): Promise<Template[]> {
    const url = `${this.environment.apiHost}/user/user/templates/list/${groupName}`;

    return lastValueFrom(this.httpClient.get(url))
      .then()
      .then((response) => {
        const list: Template[] = [];
        (response as any[]).forEach((object) => {
          const template: Template = new Template().deserialize(object);
          list.push(template);
        });
        return list;
      })
      .catch((error) => {
        this.logger.error(error);
        return Promise.reject(error);
      });
  }
  setPassword(user: User, password: string): Promise<User> {
    return new Promise<User>((resolve, reject) => {
      user = Object.assign(new User(), user);
      user.password = password;

      const url = `${this.environment.apiHost}/user/user/setpassword?deleteToken=true`;

      return lastValueFrom(this.httpClient.put(url, user))
        .then()
        .then((res: any) => {
          const updateUser = new User().deserialize(res);
          this.logger.debug("Set password for user", updateUser);
          this.user = updateUser;
          resolve(this.user);
        })
        .catch((error: HttpErrorResponse) => {
          this.logger.error(error);
          reject(error);
        });
    });
  }
  async createTemplate(template: any, tempFile: string | Blob): Promise<Template> {
    return lastValueFrom(
      this.httpClient.post(`${this.environment.apiHost}/user/user/template`, template, {
        headers: { "Content-Type": "application/json" },
      })
    )
      .then()
      .then((res: any) => {
        const imageData = new FormData();
        imageData.append("tempFile", tempFile);
        if (res && res.code && tempFile) {
          lastValueFrom(
            this.httpClient.post(`${this.environment.apiHost}/user/user/templates/${res.code}/uplaodFile`, imageData)
          ).then(
            (resp) => console.log(resp),
            (error) => console.log(error)
          );
        }
        console.log("the response is: ", res);
        return Promise.resolve(res);
      })
      .catch((error) => {
        this.logger.debug("Login failed", error.message);
        this.alertSvc.showError({ msg: error.error.fields[0].message });
      });
  }
  async getAllTemplates(): Promise<Template[]> {
    const url = `${this.environment.apiHost}/user/user/templates/list?`;

    return lastValueFrom(this.httpClient.get(url))
      .then()
      .then((response) => {
        const list: Template[] = [];
        (response as any[]).forEach((object) => {
          const template: Template = new Template().deserialize(object);
          list.push(template);
        });

        return list;
      })
      .catch((error) => {
        this.logger.error(error);
        return Promise.reject(error);
      });
  }

  async getAllTemplateReq(): Promise<Template[]> {
    // this.logger.debug('Loading users');
    const url = `${this.environment.apiHost}/user/user/templatereq/list?`;

    return lastValueFrom(this.httpClient.get(url))
      .then()
      .then((response) => {
        const list: Template[] = [];
        (response as any[]).forEach((object) => {
          const template: Template = new Template().deserialize(object);
          list.push(template);
        });

        return list;
      })
      .catch((error) => {
        this.logger.error(error);
        return Promise.reject(error);
      });
  }

  recoverPassword(username: string) {
    return new Promise<boolean>((resolve, reject) => {
      const user = new User();
      user.username = username;

      const url = `${this.environment.apiHost}/user/user/recover?deleteToken=true`;

      return lastValueFrom(this.httpClient.post(url, user)).then(
        () => {
          this.logger.debug("Recover password email sent for email", username);
          resolve(true);
        },
        (error: HttpErrorResponse) => {
          this.logger.error(error);
          reject(error);
        }
      );
    });
  }

  async searchUsers(query: string, page: number, pageSize: number, full: boolean): Promise<Pageable<User>> {
    let url = `${this.environment.apiHost}/user/user/search?query=${query}`;
    if (page > 0) {
      const offset = page - 1;
      url += `&offset=${offset}&limit=${pageSize}&`;
    }
    url += `full=${full}&sort=-lastLogin`;
    return lastValueFrom(this.httpClient.get(url))
      .then()
      .then((response: any) => {
        const list: User[] = [];
        (response.content as any[]).forEach((object) => {
          const user: User = new User().deserialize(object);
          list.push(user);
        });
        const page: Pageable<User> = new Pageable<User>().deserialize(response);
        page.content = list;
        this.logger.debug("Loaded users", page);
        return page;
      })
      .catch((error) => {
        this.logger.debug(error);
        return Promise.reject(error);
      });
  }
  async searchGroups(query: string): Promise<Group[]> {
    const url = `${this.environment.apiHost}/user/user/groups/search?query=${query}`;

    return lastValueFrom(this.httpClient.get(url))
      .then()
      .then((response) => {
        const list: Group[] = [];
        (response as any[]).forEach((object) => {
          const group: Group = new Group().deserialize(object);
          list.push(group);
        });

        return list;
      })
      .catch((error) => {
        this.logger.debug(error);
        return Promise.reject(error);
      });
  }

  async searchTemplates(query: string): Promise<Template[]> {
    const url = `${this.environment.apiHost}/user/user/templates/search?query=${query}`;

    return lastValueFrom(this.httpClient.get(url))
      .then()
      .then((response) => {
        const list: Template[] = [];
        (response as any[]).forEach((object) => {
          const template: Template = new Template().deserialize(object);
          list.push(template);
        });

        return list;
      })
      .catch((error) => {
        this.logger.debug(error);
        return Promise.reject(error);
      });
  }
  async searchPortaltemplates(query: string): Promise<Template[]> {
    const url = `${this.environment.apiHost}/user/user/portaltemplates/search?query=${query}`;

    return lastValueFrom(this.httpClient.get(url))
      .then()
      .then((response) => {
        const list: Template[] = [];
        (response as any[]).forEach((object) => {
          const template: Template = new Template().deserialize(object);
          list.push(template);
        });

        return list;
      })
      .catch((error) => {
        this.logger.debug(error);
        return Promise.reject(error);
      });
  }
  searchUsersWithTags(token: string, tags?: string[]): Promise<User[]> {
    return new Promise<User[]>((resolve, reject) => {
      let url = `${this.environment.apiHost}/user/user/search?query=${token}`;
      if (tags) {
        url += `&tags=${tags.join(",")}`;
      }
      lastValueFrom(this.httpClient.get(url)).then(
        (response: any) => {
          const list: User[] = [];
          (response.content as any[]).forEach((object) => {
            const user: User = new User().deserialize(object);
            list.push(user);
          });
          this.logger.debug("Loaded users", list);
          resolve(list);
        },
        (error) => {
          this.logger.error("Unable to search resellers", error);
          reject();
        }
      );
    });
  }

  createUser(user: User) {
    return new Promise<User>((resolve, reject) => {
      const url = `${this.environment.apiHost}/user/user/${user.id}/reload`;
      lastValueFrom(this.httpClient.post(url, {})).then(
        (res) => {
          const newUser = new User().deserialize(res);
          this.user = newUser;
          this.update$.emit(newUser);
          resolve(newUser);
        },
        (res) => {
          this.logger.debug(res.message || res);
          // console.log('error', res);
          if (res.fields) {
            const fieldErrors: any[] = [];
            (res.fields as any[]).forEach((value) => {
              fieldErrors.push(this.validationService.error(value.objectName, value.field, value.message));
            });
            reject({
              fields: fieldErrors,
            });
          } else {
            reject(res.error || res.message || res);
          }
        }
      );
    });
  }

  updateUser(user: User, _oldPassword?: string): Promise<User> {
    return new Promise<User>((resolve, reject) => {
      const url = `${this.environment.apiHost}/user/user/update`;
      lastValueFrom(this.httpClient.put(url, user)).then(
        (res) => {
          const newUser = new User().deserialize(res);
          this.user = newUser;
          this.update$.emit(newUser);
          resolve(newUser);
        },
        (reason) => {
          this.logger.debug(reason.error);
          if (reason.error.fields) {
            const fieldErrors: any[] = [];
            (reason.error.fields as any[]).forEach((value) => {
              console.log("field error", value.objectName + "." + value.field);
              fieldErrors.push(this.validationService.error(value.objectName, value.field, value.message));
            });
            reject({
              fields: fieldErrors,
            });
          } else {
            reject(reason);
          }
        }
      );
    });
  }

  updateRoles(user: User): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const url = `${this.environment.apiHost}/user/user/roles`;
      lastValueFrom(this.httpClient.put(url, user)).then(
        () => {
          resolve(true);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  saveDiscount(user: User, discount: number): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const url = `${this.environment.apiHost}/box/discount/${user.id}`;
      lastValueFrom(
        this.httpClient.post(url, {
          value: discount,
        })
      ).then(
        () => {
          resolve(true);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  deleteDiscount(user: User, discount: number): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const url = `${this.environment.apiHost}/box/discount/${user.id}?value=${discount}`;
      lastValueFrom(this.httpClient.delete(url)).then(
        () => {
          resolve(true);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  getDiscount(user: User): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      const url = `${this.environment.apiHost}/box/discount/${user.id}`;
      lastValueFrom(this.httpClient.get(url)).then(
        (res) => {
          resolve(res as number);
        },
        (error) => {
          this.logger.debug(error);
          reject(error);
        }
      );
    });
  }

  logout(): Promise<boolean> {
    return this.authService.logout().then(
      () => {
        this.user = undefined;
        return true;
      },
      () => {
        return false;
      }
    );
  }
}
